import AlertQueue from '../AlertQueue';
import giftColorThresholds from '../GiftThresholds';
import util from '../util';
import MessageWidget from './MessageWidget';
import UrlSound from '../UrlSound';
import PcmSound from '../PcmSound';

const BASE_CSS = `
.audio-resume-alert {
	position: absolute;
	top: 0; left: 0; right: 0;
	z-index: 9999;
	background: rgba(0,0,0,0.75);
	color: #fff;
	text-align: center;
	padding: 8px;
	font-size: 16px;
	cursor: pointer;
}
.widget.chat-widget {
	left: 0; right: 0; top: 0; bottom: 0;
	position: absolute;
	margin: 0; padding: 0;
	word-wrap: break-word;
	overflow-y: hidden;
	overflow-x: hidden;
}
.widget.chat-widget > .message {
	background: #008000;
	color: white;
	margin: 4px;
	padding: 8px;
	border-radius: 4px;
	list-style-type: none;
	display: block;
}
.widget.chat-widget > .message > .chatImages { float: right; }
.widget.chat-widget > .message > .chatImage  { max-width: 80px; max-height: 80px; }
.widget.chat-widget > .message .sticker,
.widget.chat-widget > .message .image  { max-width: 80px; max-height: 80px; }
.widget.chat-widget > .message .emote  { vertical-align: middle; max-width: 32px; max-height: 32px; }
.widget.chat-widget > .message .sticker { max-width: 128px; max-height: 128px; }
.widget.chat-widget > .message .platform { margin-right: 4px; }
.widget.chat-widget > .message .avatar {
	max-width: 16px; max-height: 16px;
	border-radius: 8px;
	margin-right: 4px;
	vertical-align: middle;
}
.widget.chat-widget > .message > .sticker { max-width: 80px; max-height: 80px; }
.widget.chat-widget > .message .clearfix { clear: both; }
`;

export default class ChatWidget extends MessageWidget {
	constructor(opts) {
		super(opts);

		this.queue = new AlertQueue();
		this._currentItem = null;
	}

	_build() {
		this._injectCSS('chatWidgetBaseCSS', BASE_CSS);
		this._injectGiftColorCSS();
		this._buildDOM();
		this._setupAudioMonitor();
	}

	get _widgetType() { return 'chat'; }

	_audioRequired() { return !!(this.settings.enableTTS || this.settings.enableEventSounds); }

	async init() {
		this._startScrollAnimation();
		await super.init();
	}

	destroy() {
		this._stopScrollAnimation();
		super.destroy();
	}

	// ── Settings ──────────────────────────────────────────────────────────────

	applySettings(settings) {
		const s = settings ? { ...settings } : {};
		if(typeof s.ignoreUsers === 'string')
			s.ignoreUsers = s.ignoreUsers.split('\n').filter(Boolean);
		s.ignoreUsers = (s.ignoreUsers || []).map(a => a.toLowerCase());

		const oldOutTransition = this.settings.outTransition;
		super.applySettings(s);
		this.queue.timeoutMs = s.loadTimeout || 5000;

		// remove old messages that had no out transition
		if(!oldOutTransition && settings.outTransition) {
			const messages = this.container.children;
			const ids = [];
			for(let i = 1; i < messages.length; i++) {
				if(messages[i].id)
				ids.push(messages[i].id);
			}
			for(let id of ids) this._removeMessage(id);
		}
	}

	_socketHandlers() {
		return {
			chat: data => this._onChat(data),
			userTextReplacementsChange: data => this.applyTextReplacements(data),
			userEventTypeChange: data => this.applyEventType(data.eventType, data.data),
			userGiftTypeChange: data => this.applyGiftType(data.serviceId, data.giftType, data.data),
			userGiftThresholdsChange: () => this.loadGiftThresholds(),
		};
	}

	_injectGiftColorCSS() {
		if(document.getElementById('chat-gift-color-thresholds-style')) return;
		const style = document.createElement('style');
		style.id = 'chat-gift-color-thresholds-style';
		style.textContent = giftColorThresholds.map(t => `
			.widget.chat-widget .gift.gift-${t.value} {
				color: ${util.getContrastingTextColor(t.color)};
				background-color: ${t.color};
			}
		`).join('');
		document.head.appendChild(style);
	}

	_buildDOM() {
		this.audioResumeAlert = document.createElement('div');
		this.audioResumeAlert.className = 'audio-resume-alert';
		this.audioResumeAlert.textContent = 'Click anywhere to enable audio';
		this.audioResumeAlert.hidden = true;
		this.container.parentNode?.insertBefore(this.audioResumeAlert, this.container)
			|| document.body.insertBefore(this.audioResumeAlert, this.container);

		this.container.classList.add('widget', 'chat-widget');
		this.spacer = document.createElement('div');
		this.container.appendChild(this.spacer);
		this.queue.onSizeChange = size => this._applyChatSpeedup(size);
	}

	_applyChatSpeedup(size) {
		const minSpeedup = 5;
		const baseDuration = this.settings.duration || 500;

		if(size <= minSpeedup) {
			this.container.style.setProperty('--animate-duration', `${baseDuration}ms`);
			return;
		}

		const s = 1.0 + (size - minSpeedup) / 10.0;

		// Shorten animation duration proportionally (min 50ms)
		const newDuration = Math.max(50, Math.round(baseDuration / s));
		this.container.style.setProperty('--animate-duration', `${newDuration}ms`);

		if(!this._currentItem) return;
		try {
			if(s >= 2.0) {
				this._currentItem.sounds.length = 0; // drop all sounds (including any fallback)
			} else {
				for(const snd of this._currentItem.sounds) snd?.setPlaybackRate?.(s);
			}
		} catch(e) { /* ignore */ }
	}

	_generateCSS() {
		const css = `
			.widget.chat-widget {
				padding-left: ${this.settings.paddingLeft || 0}px;
				padding-bottom: ${this.settings.paddingBottom || 0}px;
				padding-right: ${this.settings.paddingRight || 0}px;
			}
			.widget.chat-widget .message {
				${this.settings.textBg ? `background-color: ${this.settings.textBg};` : ''}
				${this.settings.textColor ? `color: ${this.settings.textColor};` : ''}
				${this.settings.fontFamily ? `font-family: ${this.settings.fontFamily};` : ''}
				${this.settings.fontSize > 0 ? `font-size: ${this.settings.fontSize}px;` : ''}
				${this.settings.letterSpacing > 0 ? `letter-spacing: ${this.settings.letterSpacing}px;` : ''}
				${this.settings.textAlign ? `text-align: ${this.settings.textAlign};` : ''}
				${this.settings.boldText ? `font-weight: bold;` : ''}
				${this.settings.italicText ? `font-style: italic;` : ''}
				${this.settings.underlineText || this.settings.strikethroughText ? `text-decoration: ${this.settings.underlineText ? 'underline' : ''} ${this.settings.strikethroughText ? 'line-through' : ''};` : ''}
				${this.settings.uppercaseText ? `text-transform: uppercase;` : ''}
				${this.settings.blurRadius > 0 || this.settings.spreadRadius > 0 ? `filter: ${util.generateSpreadShadowFilter(this.settings.spreadRadius, this.settings.blurRadius, this.settings.shadowColor)};` : ''}
			}
			.widget.chat-widget .message.isGift {
				${this.settings.giftBg ? `background-color: ${this.settings.giftBg};` : ''}
				${this.settings.giftColor ? `color: ${this.settings.giftColor};` : ''}
				-webkit-text-stroke-color: none;
				-webkit-text-stroke-width: 0;
				text-shadow: none;
			}
			.widget.chat-widget .message .avatar {
				${this.settings.fontSize > 0 ? `max-width: ${this.settings.fontSize}px;` : ''}
				${this.settings.fontSize > 0 ? `max-height: ${this.settings.fontSize}px;` : ''}
				${this.settings.fontSize > 0 ? `border-radius: ${Math.ceil(this.settings.fontSize / 2)}px;` : ''}
			}
			.widget.chat-widget > .message .chatImage {
				${this.settings.fontSize > 0 ? `max-width: ${this.settings.fontSize * 3}px;` : ''}
				${this.settings.fontSize > 0 ? `max-height: ${this.settings.fontSize * 3}px;` : ''}
			}
			${this.settings.enableSpeakingHighlight && this.settings.speakingOutlineColor ? `
			.widget.chat-widget > .message.speaking {
				filter: ${util.generateSpreadShadowFilter(this.settings.spreadRadius || 2, this.settings.blurRadius || 10, this.settings.speakingOutlineColor)};
			}` : ''}
			${this.settings.enableLoadingHighlight && this.settings.loadingOutlineColor ? `
			.widget.chat-widget > .message.loading {
				filter: ${util.generateSpreadShadowFilter(this.settings.spreadRadius || 2, this.settings.blurRadius || 10, this.settings.loadingOutlineColor)};
			}` : ''}
			:root { --animate-duration: ${this.settings.duration || 500}ms; }
			${this.settings.extraCSS || ''}
		`;
		this._setCSS('chatWidgetStyle', css);
		this._updateFontLink();
	}

	// ── Chat message handling ─────────────────────────────────────────────────

	_onChat(data) {
		if(data.type === 'delete') {
			for(const msgId of data.ids) {
				const itemId = `${data.serviceId}-${msgId}`;
				const removed = this.queue.cancel(it => it.prepared.id === itemId);
				for(const item of removed) item.sounds?.forEach(s => s.stop());
				this._removeMessage(itemId, true);
			}
			return;
		} else if(data.type == 'clear') {
			const removed = this.queue.cancel(it => it.prepared.serviceId === data.serviceId && it.prepared.channelToken == data.target?.channelToken && it.prepared.chatToken == data.target?.chatToken);
			for(const item of removed) item.sounds?.forEach(s => s.stop());
			for(let i = 0; i < this.container.childNodes.length; i++) {
				const child = this.container.childNodes[i];
				if(child.dataset.serviceId == data.serviceId && child.dataset.channelToken == data.target?.channelToken && child.dataset.chatToken == data.target?.chatToken)
					this.container.childNodes[i--].remove();
			}

			return;
		}

		if(this._channelHasFlag(data.serviceId, data.target?.channelToken, 'no-chat')) return;

		const displayName = data.sender?.displayName || data.sender?.username || data.sender?.token || '';
		if(displayName && (this.settings.ignoreUsers || []).indexOf(displayName.toLowerCase()) >= 0) return;

		const prepared = util.prepareMessage(data, {
			id: true, serviceId: true, serviceName: true, serviceIconUrl: true,
			channelToken: true, chatToken: true,
			avatarUrl: true, displayName: true, spansHtml: true, giftString: true,
			eventString: true, classList: true, giftColorThreshold: true,
			sounds: true,
			group: true, groupId: true, images: true, consoleLog: true,
		}, {
			giftTypes: this.giftTypes,
			eventTypes: this.eventTypes,
			serviceIcons: this.serviceIcons,
			user: this.userData,
			giftColorThresholds,
			giftMediaThresholds: this.giftThresholds,
			textReplacements: this.textReplacements,
		});

		console.log.apply(null, prepared.consoleLog);

		if(prepared.group) {
			const allMessages = Array.from(this.container.children);
			const lastInGroup = allMessages.findLast(el => el.dataset.groupId === prepared.groupId);
			if(lastInGroup) {
				lastInGroup.dataset.multiplier = (parseInt(lastInGroup.dataset.multiplier || '1') + (data.multiplier || 1)).toString();
				const giftElem = lastInGroup.querySelector('.gift-string');
				if(giftElem) giftElem.textContent = ` ${util.generateGiftString(data, this.giftTypes?.[data.serviceId]?.[data.giftType])}`;
				return;
			}
		}

		for(let i = 0; i < 10; i++) {
			if(!document.getElementById(prepared.id)) break;
			prepared.id = util.generateRandomString(20);
		}
		prepared.classList.push('animate__animated');

		const maxMessages = 50;
		const messages = this.container.children;
		while(messages.length > maxMessages) {
			const toRemove = messages[1]; // messages[0] is the spacer
			const next = messages[2];
			const h = next ? next.offsetTop - toRemove.offsetTop : toRemove.offsetHeight;
			this.spacer.style.height = (this.spacer.offsetHeight + h) + 'px';
			toRemove.remove();
		}

		const msgElem = this._createMessageElement(prepared);
		if(this.settings.inTransition) {
			msgElem.classList.add(`animate__${this.settings.inTransition}`);
		}
		this.container.appendChild(msgElem);

		const item = { prepared, elem: msgElem };

		this.queue.append({
			data:    item,
			preload: signal => this._preloadChat(item, signal),
			play:    signal => this._processChat(item, signal),
		});
	}

	async _preloadChat(item, signal) {
		if(this.queue.pendingItems.length >= 15) return;
		if(!this.settings.enableTTS && !this.settings.enableEventSounds) return;

		item.elem.classList.add('loading');
		try {
			const loadSounds = [];
			const loadSoundsRecursive = async sounds => {
				try {
					if(Array.isArray(sounds)) {
						for(let sound of sounds) {
							if(await loadSoundsRecursive(sound)) return true;
						}
						throw new Error('Could not load a sound');
					}

					if(sounds.type == 'any') {
						let loaded = 0;
						for(let sound of sounds.items) {
							if(await loadSoundsRecursive(sound)) loaded++;
						}
						if(!loaded) throw new Error('Could not load any sound');
						return true;
					}

					let category = 'event';
					if(sounds.type == 'tts') {
						if(!this.settings.enableTTS) return false;
						category = 'tts';
					} else if(sounds.type == 'song') {
						throw new Error(`Unknown sound type ${sounds.type}`);
					} else {
						if(!this.settings.enableEventSounds) return false;
					}

					let p;
					if(sounds.url) p = new UrlSound(this.soundManager, this.soundCache, sounds.url);
					else if(sounds.type == 'tts') {
						p = new PcmSound(this.soundManager, this.soundCache, signal => this.voiceApi.synthesizeSpeech(sounds.speak, sounds.provider, sounds.voice, sounds.rate, sounds.pitch, sounds.style, sounds.role, signal));
					} else if(sounds.type == 'mml') {
						p = new PcmSound(this.soundManager, this.soundCache, signal => this.mmlApi.synthesizeMusic(sounds.mml, sounds.instrument, signal));
					}
					p.category = category;
					await p.load(signal);
					loadSounds.push(p);
					return true;
				} catch(e) {
					console.error('Could not load sound', sounds, e);
					return false;
				}
			};
			try {
				await loadSoundsRecursive(item.prepared.sounds);
				item.sounds = loadSounds;
			} catch(e) {
				console.error('Could not load any sounds', e);
			}
		} finally {
			item.elem.classList.remove('loading');
		}
	}

	async _processChat(item, signal) {
		item.elem.classList.add('speaking');
		this._currentItem = item;
		this._applyChatSpeedup(this.queue.pendingItems.length);
		try {
			if(this.queue.pendingItems.length >= 15) throw 'queue full';
			if(!this.settings.enableTTS && !this.settings.enableEventSounds) throw 'sounds disabled';
			if(!item.sounds) throw 'no sounds';
			for(const s of item?.sounds) {
				if(s.category == 'tts' && !this.settings.enableTTS) continue;
				if(s.category == 'event' && !this.settings.enableEventSounds) continue;
				try { await s.play(signal); } catch(e) {
					if(!(e instanceof DOMException && e.name === 'AbortError')) console.error('Could not play sound', s, e);
				}
				if(signal?.aborted) break;
			}
		} catch(e) {
			//
		} finally {
			this._currentItem = null;
		}
		item.elem.classList.remove('speaking');

		if(this.settings.outTransition)
			setTimeout(() => this._removeMessage(item.prepared.id), this.settings.linger || 8000);
	}

	// ── DOM helpers ───────────────────────────────────────────────────────────

	_createMessageElement(prepared) {
		const div = document.createElement('div');
		div.id = prepared.id;
		if(prepared.groupId) div.dataset.groupId = prepared.groupId;
		div.dataset.multiplier = '1';
		for(const cls of (prepared.classList || [])) div.classList.add(cls);
		div.dataset.serviceId = prepared.serviceId;
		div.dataset.channelToken = prepared.channelToken;
		div.dataset.chatToken = prepared.chatToken;

		if(prepared.serviceIconUrl) {
			const icon = document.createElement('img');
			icon.src = prepared.serviceIconUrl;
			icon.alt = prepared.serviceName || '';
			icon.className = 'avatar';
			div.appendChild(icon);
		} else if(prepared.serviceName) {
			const small = document.createElement('small');
			small.className = 'platform';
			small.textContent = prepared.serviceName;
			div.appendChild(small);
		}

		if(prepared.images?.length) {
			const imagesDiv = document.createElement('div');
			imagesDiv.className = 'chatImages';
			for(const img of prepared.images) {
				if(!img?.url) continue;
				const imgElem = document.createElement('img');
				imgElem.src = img.url;
				imgElem.alt = '';
				imgElem.className = 'chatImage';
				imagesDiv.appendChild(imgElem);
			}
			div.appendChild(imagesDiv);
		}

		if(prepared.avatarUrl) {
			const avatar = document.createElement('img');
			avatar.src = prepared.avatarUrl;
			avatar.alt = '';
			avatar.className = 'avatar';
			div.appendChild(avatar);
		}

		const name = document.createElement('b');
		name.textContent = prepared.displayName;
		div.appendChild(name);

		if(prepared.giftString) {
			const giftElem = document.createElement('b');
			giftElem.className = 'gift-string';
			giftElem.textContent = ` ${prepared.giftString}`;
			div.appendChild(giftElem);
		}

		if(prepared.eventString) {
			const eventElem = document.createElement('span');
			eventElem.textContent = ` ${prepared.eventString}`;
			div.appendChild(eventElem);
		}

		if(prepared.spansHtml) {
			const spansElem = document.createElement('span');
			spansElem.innerHTML = `: ${prepared.spansHtml}`;
			div.appendChild(spansElem);
		}

		const clearfix = document.createElement('div');
		clearfix.className = 'clearfix';
		div.appendChild(clearfix);

		return div;
	}

	_removeMessage(id, force) {
		const el = document.getElementById(id);
		if(!el) return;
		if(this.settings.outTransition && !force) {
			el.classList.remove(`animate__${this.settings.inTransition}`);
			// Snapshot layout before taking element out of flow
			const top = el.offsetTop;
			const left = el.offsetLeft;
			const width = el.offsetWidth;
			const height = el.offsetHeight;
			// Add to spacer now — it starts shrinking while the animation plays
			this.spacer.style.height = (this.spacer.offsetHeight + height) + 'px';
			// Pin element in place visually while it plays its exit animation
			el.style.position = 'absolute';
			el.style.top = top + 'px';
			el.style.left = left + 'px';
			el.style.width = width + 'px';
			el.classList.add(`animate__${this.settings.outTransition}`);
			const fallback = setTimeout(() => el.remove(), (this.settings.duration || 500) + 500);
			el.addEventListener('animationend', () => { clearTimeout(fallback); el.remove(); }, { once: true });
		} else {
			this.spacer.style.height = (this.spacer.offsetHeight + el.offsetHeight) + 'px';
			el.remove();
		}
	}

	// ── Scroll animation ──────────────────────────────────────────────────────

	_startScrollAnimation() {
		const frame = () => {
			if(!this._animFrameId) return;
			this._animFrameId = requestAnimationFrame(frame);
			const el = this.container;
			const spacerH = this.spacer.offsetHeight;
			if(spacerH > 0) {
				this.spacer.style.height = Math.max(0, spacerH - Math.round(spacerH / 5) - 1) + 'px';
			}
			const last = el.lastElementChild;
			if(!last) return;
			const paddingBottom = parseInt(getComputedStyle(el).paddingBottom) || 0;
			const target = last.offsetTop + last.offsetHeight + paddingBottom - el.clientHeight;
			const diff = target - el.scrollTop;
			if(diff > 0) el.scrollTop += Math.round(diff / 5) + 1;
		};
		this._animFrameId = requestAnimationFrame(frame);
	}

	_stopScrollAnimation() {
		if(this._animFrameId) {
			cancelAnimationFrame(this._animFrameId);
			this._animFrameId = null;
		}
	}
}
